import React, { Component } from "react";
import ConversionsService from "../../services/conversionsservice";

// CRM > Conversions — two tabs:
//  • Registrations: registration-cohort funnel (customers who registered in the
//    window, and how many reached their 1st/2nd/3rd/4+ order; step conversions,
//    optional order-window cap for fair cohort comparison).
//  • Conversion: order-date activity — of the orders placed in the window, how
//    many were someone's 1st/2nd/3rd/4+ order (share of period orders).
// Both tabs support period presets + custom range + period-over-period compare,
// and a set of SVG charts. Reads Admin_CRM_Conversions* procs. Read-only.

const PAGE_SIZE = 50;
const STAGES = [
  { k: "1", label: "1st Order", dot: "#d6136e" },
  { k: "2", label: "2nd Order", dot: "#5b46e5" },
  { k: "3", label: "3rd Order", dot: "#c77a08" },
  { k: "plus", label: "3+ Orders", dot: "#12a150" },
];
const META = {
  reg: { "1": "of registrations", "2": "of 1st-order customers", "3": "of 2nd-order customers", "plus": "of 3rd-order customers" },
  ord: { "1": "were 1st orders", "2": "were 2nd orders", "3": "were 3rd orders", "plus": "were 4+ orders" },
  rev: { "1": "from 1st orders", "2": "from 2nd orders", "3": "from 3rd orders", "plus": "from 4+ orders" },
};
const TBL = {
  reg: { "1": "their 1st order", "2": "their 2nd order", "3": "their 3rd order", "plus": "4+ orders" },
  ord: { "1": "a 1st order", "2": "a 2nd order", "3": "a 3rd order", "plus": "a 4+ order" },
  rev: { "1": "a 1st order", "2": "a 2nd order", "3": "a 3rd order", "plus": "a 4+ order" },
};
const PERIODS = [
  { k: "all", label: "All time" }, { k: "month", label: "This month" },
  { k: "qtr", label: "This quarter" }, { k: "year", label: "This year" }, { k: "custom", label: "Custom" },
];
const WINDOWS = [{ k: 0, label: "Lifetime" }, { k: 30, label: "First 30d" }, { k: 60, label: "First 60d" }, { k: 90, label: "First 90d" }];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const inr = (n) => "₹" + Math.round(Number(n) || 0).toLocaleString("en-IN");
const nfmt = (n) => (Number(n) || 0).toLocaleString("en-IN");
const initc = (nm) => (nm || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
const AV = ["#d6136e", "#5b46e5", "#12a150", "#c77a08", "#0ea5a5", "#7c3aed", "#e11d48", "#2563eb"];
const fmtD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const stripT = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const dayMs = 86400000;

export default class Conversions extends Component {
  constructor(props) {
    super(props);
    this.service = new ConversionsService();
    this.state = {
      tab: "reg", summary: null, summaryB: null, rows: [], trend: [],
      bucket: "1", page: 1, total: 0,
      period: "all", from: "", to: "", cfrom: "", cto: "", compare: false, maxDays: 0, search: "",
      loading: true, loadingRows: true,
    };
  }
  componentDidMount() { this.loadSummary(); this.loadRows(); this.loadTrend(); }
  storeID() { return 0; }
  isOrd() { return this.state.tab === "ord"; }
  isRev() { return this.state.tab === "rev"; }
  byOrder() { return this.state.tab === "ord" || this.state.tab === "rev"; }
  totalRev(s) { s = s || {}; return (+s.value1 || 0) + (+s.value2 || 0) + (+s.value3 || 0) + (+s.valuePlus || 0); }
  primVal(s, k) { return this.isRev() ? this.valueOf(s, k) : this.countOf(s, k); }
  primFmt(x) { return this.isRev() ? inr(x) : nfmt(x); }

  rangeA(period) {
    const now = new Date(), y = now.getFullYear(), m = now.getMonth();
    if (period === "all") return { from: "", to: "" };
    if (period === "month") return { from: fmtD(new Date(y, m, 1)), to: fmtD(now) };
    if (period === "qtr") return { from: fmtD(new Date(y, Math.floor(m / 3) * 3, 1)), to: fmtD(now) };
    if (period === "year") return { from: fmtD(new Date(y, 0, 1)), to: fmtD(now) };
    return { from: this.state.from, to: this.state.to };
  }
  rangeB() {
    const period = this.state.period, now = new Date();
    if (period === "custom") return (this.state.cfrom && this.state.cto) ? { from: this.state.cfrom, to: this.state.cto } : null;
    let aStart;
    if (period === "month") aStart = new Date(now.getFullYear(), now.getMonth(), 1);
    else if (period === "qtr") aStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    else if (period === "year") aStart = new Date(now.getFullYear(), 0, 1);
    else return null;
    const elapsed = Math.round((stripT(now) - stripT(aStart)) / dayMs);
    let bStart;
    if (period === "month") bStart = new Date(aStart.getFullYear(), aStart.getMonth() - 1, 1);
    else if (period === "qtr") bStart = new Date(aStart.getFullYear(), aStart.getMonth() - 3, 1);
    else bStart = new Date(aStart.getFullYear() - 1, 0, 1);
    const bEnd = new Date(bStart); bEnd.setDate(bEnd.getDate() + elapsed);
    return { from: fmtD(bStart), to: fmtD(bEnd) };
  }
  canCompare() { return ["month", "qtr", "year", "custom"].indexOf(this.state.period) >= 0; }
  compareOn() { return this.state.compare && this.canCompare() && this.state.summaryB; }

  sGet(f, t) {
    return this.byOrder()
      ? this.service.getByOrderSummary(f, t, this.storeID())
      : this.service.getSummary(f, t, this.storeID(), this.state.maxDays);
  }
  loadSummary = async () => {
    this.setState({ loading: true });
    const a = this.rangeA(this.state.period);
    const s = await this.sGet(a.from || null, a.to || null);
    let sB = null;
    if (this.state.compare && this.canCompare()) { const b = this.rangeB(); if (b) sB = await this.sGet(b.from, b.to); }
    this.setState({ summary: s || {}, summaryB: sB, loading: false });
  };
  loadRows = async () => {
    this.setState({ loadingRows: true });
    const a = this.rangeA(this.state.period);
    const r = this.byOrder()
      ? await this.service.getByOrderList(this.state.bucket, this.state.search, a.from || null, a.to || null, this.storeID(), this.state.page, PAGE_SIZE)
      : await this.service.getList(this.state.bucket, this.state.search, a.from || null, a.to || null, this.storeID(), this.state.page, PAGE_SIZE, this.state.maxDays);
    const rows = Array.isArray(r) ? r : [];
    this.setState({ rows, total: rows.length ? rows[0].totalRows : 0, loadingRows: false });
  };
  loadTrend = async () => { const t = await this.service.getTrend(this.storeID(), 12); this.setState({ trend: Array.isArray(t) ? t : [] }); };
  reloadAll = () => { this.loadSummary(); this.loadRows(); };

  monthEdge(k, end) {
    const n = new Date();
    return fmtD(end ? new Date(n.getFullYear(), n.getMonth() + k + 1, 0) : new Date(n.getFullYear(), n.getMonth() + k, 1));
  }
  setTab = (tab) => { if (tab !== this.state.tab) this.setState({ tab, page: 1, bucket: "1" }, this.reloadAll); };
  setPeriod = (period) => {
    const st = this.state;
    let from, to, cfrom = st.cfrom, cto = st.cto;
    if (period === "custom") {
      from = st.from || this.monthEdge(0, false); to = st.to || fmtD(new Date());
      if (!cfrom) cfrom = this.monthEdge(-1, false); if (!cto) cto = this.monthEdge(-1, true);
    } else { const a = this.rangeA(period); from = a.from; to = a.to; }
    const compare = (period === "all") ? false : st.compare;
    this.setState({ period, from, to, cfrom, cto, page: 1, compare }, this.reloadAll);
  };
  setCustom = (field, val) => { this.setState({ [field]: val }); };
  applyCustom = () => { let { from, to } = this.state; if (from && to && to < from) { const t = from; from = to; to = t; } if (from && to) this.setState({ from, to, page: 1 }, this.reloadAll); };
  applyCustomB = () => { let { cfrom, cto } = this.state; if (cfrom && cto && cto < cfrom) { const t = cfrom; cfrom = cto; cto = t; } if (cfrom && cto) this.setState({ cfrom, cto }, this.loadSummary); };
  setWindow = (d) => { if (d !== this.state.maxDays) this.setState({ maxDays: d, page: 1 }, this.reloadAll); };
  toggleCompare = () => {
    if (!this.canCompare()) return;
    const turningOn = !this.state.compare, patch = { compare: turningOn };
    if (turningOn && this.state.period === "custom") { if (!this.state.cfrom) patch.cfrom = this.monthEdge(-1, false); if (!this.state.cto) patch.cto = this.monthEdge(-1, true); }
    this.setState(patch, this.loadSummary);
  };
  setBucket = (k) => { if (k !== this.state.bucket) this.setState({ bucket: k, page: 1 }, this.loadRows); };
  setPage = (p) => { this.setState({ page: p }, this.loadRows); };
  onSearch = (v) => { this.setState({ search: v, page: 1 }); };
  runSearch = () => { this.setState({ page: 1 }, this.loadRows); };

  countOf(s, k) { s = s || {}; return k === "1" ? (+s.bucket1 || 0) : k === "2" ? (+s.bucket2 || 0) : k === "3" ? (+s.bucket3 || 0) : (+s.bucketPlus || 0); }
  valueOf(s, k) { s = s || {}; return k === "1" ? (+s.value1 || 0) : k === "2" ? (+s.value2 || 0) : k === "3" ? (+s.value3 || 0) : (+s.valuePlus || 0); }
  pctVal(s, k) {
    s = s || {};
    if (this.isRev()) { const tr = this.totalRev(s); return tr > 0 ? 100 * this.valueOf(s, k) / tr : null; }
    if (this.isOrd()) { const tot = +s.totalOrders || 0; return tot > 0 ? 100 * this.countOf(s, k) / tot : null; }
    const reg = +s.registered || 0, r1 = +s.bucket1 || 0, r2 = +s.bucket2 || 0, r3 = +s.bucket3 || 0, r4 = +s.bucketPlus || 0;
    let num, den;
    if (k === "1") { num = r1; den = reg; } else if (k === "2") { num = r2; den = r1; } else if (k === "3") { num = r3; den = r2; } else { num = r4; den = r3; }
    return den > 0 ? (100 * num / den) : null;
  }
  fmtPct = (x) => (x == null ? "–" : (Math.round(x * 10) / 10).toFixed(1) + "%");
  rangeLabel(f, t) {
    if (!f || !t) return "—";
    const [fy, fm, fd] = f.split("-").map(Number), [ty, tm, td] = t.split("-").map(Number);
    const lastDay = new Date(fy, fm, 0).getDate();
    if (fd === 1 && fy === ty && fm === tm && td === lastDay) return `${MON[fm - 1]} ${fy}`;
    const s = `${fd} ${MON[fm - 1]}`, e = `${td} ${MON[tm - 1]}`;
    return fy === ty ? `${s} – ${e}` : `${s} ${fy} – ${e} ${ty}`;
  }
  labelA() { const p = this.state.period; if (p === "month") return "This month"; if (p === "qtr") return "This quarter"; if (p === "year") return "This year"; if (p === "all") return "All time"; const a = this.rangeA("custom"); return this.rangeLabel(a.from, a.to); }
  labelB() { const p = this.state.period; if (p === "month") return "Last month"; if (p === "qtr") return "Last quarter"; if (p === "year") return "Last year"; return this.rangeLabel(this.state.cfrom, this.state.cto); }

  // ---- charts (inline SVG) ----
  chartFunnel() {
    const s = this.state.summary || {}, base = this.state.tab === "reg";
    const rows = (base ? [{ lb: "Registered", v: +s.registered || 0, c: "#8a94a6" }] : [])
      .concat(STAGES.map((st) => ({ lb: st.label, v: this.primVal(s, st.k), c: st.dot })));
    const max = Math.max(1, ...rows.map((r) => r.v));
    const rh = 22, gap = 10, x0 = 82, W = 330, bw = W - x0 - 62, H = rows.length * (rh + gap);
    return (
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W }} role="img">
        {rows.map((r, i) => { const y = i * (rh + gap), w = Math.max(2, bw * r.v / max); return (
          <g key={i}>
            <text x={x0 - 7} y={y + rh / 2 + 4} textAnchor="end" fontSize="11" fill="#69707e">{r.lb}</text>
            <rect x={x0} y={y} width={w} height={rh} rx="4" fill={r.c} opacity="0.9" />
            <text x={x0 + w + 6} y={y + rh / 2 + 4} fontSize="10.5" fontWeight="700" fill="#1d2330">{this.primFmt(r.v)}</text>
          </g>); })}
      </svg>
    );
  }
  chartPct() {
    const bars = STAGES.map((st) => ({ lb: st.label, v: this.pctVal(this.state.summary, st.k) || 0, c: st.dot }));
    const max = Math.max(1, ...bars.map((b) => b.v));
    const n = bars.length, W = 330, H = 160, pad = 10, gh = H - 40, y0 = 10, gw = (W - pad * 2) / n;
    return (
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W }} role="img">
        {bars.map((b, i) => { const cx = pad + i * gw + gw / 2, bw = gw * 0.5, h = gh * b.v / max; const y = y0 + gh - h; return (
          <g key={i}>
            <rect x={cx - bw / 2} y={y} width={bw} height={Math.max(1, h)} rx="4" fill={b.c} opacity="0.9" />
            <text x={cx} y={y - 4} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#1d2330">{this.fmtPct(b.v)}</text>
            <text x={cx} y={H - 8} textAnchor="middle" fontSize="10" fill="#69707e">{b.lb.replace(" Orders", "+").replace(" Order", "")}</text>
          </g>); })}
      </svg>
    );
  }
  chartGrouped() {
    const on = this.compareOn();
    const bars = STAGES.map((st) => ({ lb: st.label, a: this.primVal(this.state.summary, st.k), b: on ? this.primVal(this.state.summaryB, st.k) : 0, c: st.dot }));
    const max = Math.max(1, ...bars.map((b) => Math.max(b.a, b.b)));
    const n = bars.length, W = 330, H = 160, pad = 10, gh = H - 40, y0 = 10, gw = (W - pad * 2) / n;
    return (
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W }} role="img">
        {bars.map((bar, i) => {
          const cx = pad + i * gw + gw / 2, bw = on ? gw * 0.32 : gw * 0.5;
          const ha = gh * bar.a / max, hb = gh * bar.b / max;
          return (<g key={i}>
            {on ? (<>
              <rect x={cx - bw - 2} y={y0 + gh - ha} width={bw} height={Math.max(1, ha)} rx="3" fill={bar.c} opacity="0.9" />
              <rect x={cx + 2} y={y0 + gh - hb} width={bw} height={Math.max(1, hb)} rx="3" fill="#c7ccd6" />
            </>) : <rect x={cx - bw / 2} y={y0 + gh - ha} width={bw} height={Math.max(1, ha)} rx="3" fill={bar.c} opacity="0.9" />}
            <text x={cx} y={H - 8} textAnchor="middle" fontSize="10" fill="#69707e">{bar.lb.replace(" Orders", "+").replace(" Order", "")}</text>
          </g>);
        })}
      </svg>
    );
  }
  chartTrend() {
    const t = this.state.trend || [];
    if (!t.length) return <div className="cv-chempty">No trend data yet</div>;
    const money = this.byOrder(), key = money ? "revenue" : "registrations";
    const vals = t.map((r) => +r[key] || 0), max = Math.max(1, ...vals);
    const W = 340, H = 160, padL = 10, padR = 10, padT = 12, padB = 24, gw = W - padL - padR, gh = H - padT - padB, n = t.length;
    const X = (i) => padL + (n <= 1 ? gw / 2 : gw * i / (n - 1));
    const Y = (v) => padT + gh - gh * v / max;
    const pts = vals.map((v, i) => `${X(i)},${Y(v)}`).join(" ");
    const area = `${padL},${padT + gh} ${pts} ${X(n - 1)},${padT + gh}`;
    const step = Math.max(1, Math.ceil(n / 6));
    return (
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W }} role="img">
        <polyline points={area} fill="#5b46e5" opacity="0.08" stroke="none" />
        <polyline points={pts} fill="none" stroke="#5b46e5" strokeWidth="2" />
        {vals.map((v, i) => <circle key={i} cx={X(i)} cy={Y(v)} r="2.6" fill="#5b46e5" />)}
        {t.map((r, i) => i % step === 0 ? <text key={i} x={X(i)} y={H - 8} textAnchor="middle" fontSize="9" fill="#9aa1af">{MON[(+r.ym.slice(5, 7)) - 1]}</text> : null)}
      </svg>
    );
  }

  render() {
    const { bucket, rows, summary, summaryB, page, total, period, compare, maxDays, cfrom, cto, tab, loading, loadingRows } = this.state;
    const ord = this.isOrd(), rev = this.isRev();
    const reg = (summary && +summary.registered) || 0;
    const regB = (summaryB && +summaryB.registered) || 0;
    const totOrdA = (summary && +summary.totalOrders) || 0, totOrdB = (summaryB && +summaryB.totalOrders) || 0;
    const cnt = this.countOf(summary, bucket), val = this.valueOf(summary, bucket);
    const avg = cnt ? val / cnt : 0;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const on = this.compareOn();
    const a = this.rangeA(period), b = on ? this.rangeB() : null;
    const dlt = (this.pctVal(summary, bucket) != null && this.pctVal(summaryB, bucket) != null) ? Math.round((this.pctVal(summary, bucket) - this.pctVal(summaryB, bucket)) * 10) / 10 : null;
    const aLabel = this.labelA(), bLabel = this.labelB();
    const baseStr = (s, r, tord) => rev ? (inr(this.totalRev(s)) + " revenue") : (this.byOrder() ? (nfmt(tord) + " orders") : (nfmt(r) + " registered"));
    const baseA = baseStr(summary, reg, totOrdA);
    const baseB = baseStr(summaryB, regB, totOrdB);

    return (
      <div className="cv-wrap">
        <style>{CSS}</style>

        <h1 className="cv-h1"><span className="cv-ey">CRM ·</span> Conversions</h1>

        <div className="cv-tabs">
          <button className={tab === "reg" ? "on" : ""} onClick={() => this.setTab("reg")}>Registrations</button>
          <button className={tab === "ord" ? "on" : ""} onClick={() => this.setTab("ord")}>Conversion</button>
          <button className={tab === "rev" ? "on" : ""} onClick={() => this.setTab("rev")}>Revenue</button>
        </div>

        <p className="cv-sub">
          {tab === "reg"
            ? "Registration cohort: the date range picks customers who registered in that window, then each stage shows how many reached their 1st, 2nd, 3rd or 4+ order, with the step-to-step conversion."
            : tab === "ord"
              ? "Order-date activity: of the orders placed in the window, how many were a customer's 1st, 2nd, 3rd or 4+ order. Shows conversion volume happening in the period."
              : "Revenue by order stage: of the billed value in the window, how much came from customers' 1st, 2nd, 3rd or 4+ orders — with each stage's share of period revenue."}
          {(tab === "reg" && maxDays) ? <span className="cv-regnote"> · orders within the <b>first {maxDays} days</b> of registration</span> : null}
        </p>

        <div className="cv-filters">
          <span className="cv-flabel">Period</span>
          <div className="cv-seg">
            {PERIODS.map((p) => <button key={p.k} className={period === p.k ? "on" : ""} onClick={() => this.setPeriod(p.k)}>{p.label}</button>)}
          </div>
          {period === "custom" ? (
            <span className="cv-custom">
              <input type="date" value={this.state.from} onChange={(e) => this.setCustom("from", e.target.value)} />
              <span className="cv-arw">→</span>
              <input type="date" value={this.state.to} onChange={(e) => this.setCustom("to", e.target.value)} />
              <button className="cv-apply" onClick={this.applyCustom}>Apply</button>
            </span>
          ) : a.from ? <span className="cv-daterng">{a.from} → {a.to}</span> : null}
        </div>

        <div className="cv-filters">
          {tab === "reg" ? (<>
            <span className="cv-flabel">Order window</span>
            <div className="cv-seg">
              {WINDOWS.map((w) => <button key={w.k} className={maxDays === w.k ? "on" : ""} onClick={() => this.setWindow(w.k)}>{w.label}</button>)}
            </div>
          </>) : <span className="cv-flabel">Compare</span>}
          <label className={"cv-cmp-tog" + (this.canCompare() ? "" : " off")}>
            <input type="checkbox" checked={compare && this.canCompare()} disabled={!this.canCompare()} onChange={this.toggleCompare} />
            {period === "custom" ? "Compare vs another range" : "Compare vs previous " + (period === "month" ? "month" : period === "qtr" ? "quarter" : period === "year" ? "year" : "period")}
          </label>
          {compare && period === "custom" ? (
            <span className="cv-custom">
              <span className="cv-arw">vs</span>
              <input type="date" value={cfrom} onChange={(e) => this.setCustom("cfrom", e.target.value)} />
              <span className="cv-arw">→</span>
              <input type="date" value={cto} onChange={(e) => this.setCustom("cto", e.target.value)} />
              <button className="cv-apply" onClick={this.applyCustomB}>Apply</button>
            </span>
          ) : on && b ? <span className="cv-daterng cv-vs">vs {b.from} → {b.to}</span> : null}
        </div>

        <div className="cv-stages">
          {STAGES.map((st) => {
            const pa = this.pctVal(summary, st.k), pb = this.pctVal(summaryB, st.k);
            const d = (pa != null && pb != null) ? Math.round((pa - pb) * 10) / 10 : null;
            return (
              <button key={st.k} className="cv-stage" aria-selected={bucket === st.k} onClick={() => this.setBucket(st.k)}>
                {!on ? <span className="cv-pct" style={{ color: st.dot }}>{loading ? "" : this.fmtPct(pa)}</span> : null}
                <div className="cv-lab"><span className="cv-dot" style={{ background: st.dot }} />{st.label}</div>
                {on ? (
                  <table className="cv-mini"><tbody>
                    <tr><td className="lb">{aLabel}</td><td className="cn">{this.primFmt(this.primVal(summary, st.k))}</td><td className="pv" style={{ color: st.dot }}>{this.fmtPct(pa)}</td><td></td></tr>
                    <tr><td className="lb">{bLabel}</td><td className="cn">{this.primFmt(this.primVal(summaryB, st.k))}</td><td className="pv">{this.fmtPct(pb)}</td>
                      <td className="dl">{d != null ? <span className={"cv-delta " + (d > 0 ? "up" : d < 0 ? "down" : "")}>{d > 0 ? "▲" : d < 0 ? "▼" : "•"} {Math.abs(d).toFixed(1)}</span> : null}</td></tr>
                  </tbody></table>
                ) : (<>
                  <div className="cv-n">{loading ? "–" : this.primFmt(this.primVal(summary, st.k))}</div>
                  <div className="cv-meta">{loading ? "" : <span className="cv-pctnum">{this.fmtPct(pa)}</span>} {META[tab][st.k]}</div>
                </>)}
              </button>
            );
          })}
        </div>

        <div className="cv-charts">
          <div className="cv-chart"><div className="cv-cht">Funnel — {rev ? "revenue by stage" : ord ? "orders by stage" : "cohort by stage"}</div>{this.chartFunnel()}</div>
          <div className="cv-chart"><div className="cv-cht">{rev ? "Share of period revenue" : ord ? "Share of period orders" : "Step conversion %"}</div>{this.chartPct()}</div>
          <div className="cv-chart"><div className="cv-cht">{on ? aLabel + " vs " + bLabel : (rev ? "Revenue by stage" : ord ? "Orders by stage" : "Customers by stage")}</div>{this.chartGrouped()}</div>
          <div className="cv-chart"><div className="cv-cht">{this.byOrder() ? "Revenue / month" : "Registrations / month"} · 12 mo</div>{this.chartTrend()}</div>
        </div>

        <section className="cv-card">
          <div className="cv-cardhd">
            <h2>{this.byOrder() ? "Customers who placed " + TBL[tab][bucket] + " in this period" : "Customers who reached " + TBL.reg[bucket]}</h2>
            <span className="cv-desc">· {baseA}{on ? " (" + aLabel + ") · " + bLabel + " " + baseB : ""}</span>
            <div className="cv-right">
              <input className="cv-search" placeholder="Search name / mobile" onChange={(e) => this.onSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") this.runSearch(); }} />
              <button className="cv-exp" onClick={this.runSearch}>Search</button>
            </div>
          </div>

          <div className="cv-strip">
            <div className="cv-kpi"><div className="l">{rev ? "Revenue" : ord ? "Conversions" : "Customers"}</div><div className="v">{rev ? inr(val) : nfmt(cnt)}</div></div>
            <div className="cv-kpi"><div className="l">{rev ? "Orders" : "Order value"}</div><div className="v">{rev ? nfmt(cnt) : inr(val)}</div></div>
            <div className="cv-kpi"><div className="l">Avg / {tab === "reg" ? "customer" : "order"}</div><div className="v">{inr(avg)}</div></div>
            <div className="cv-kpi"><div className="l">{tab === "reg" ? "Conversion" : "Share"}</div>
              <div className="v">{this.fmtPct(this.pctVal(summary, bucket))}
                {on && dlt != null ? <span className={"cv-delta " + (dlt > 0 ? "up" : dlt < 0 ? "down" : "")}>{dlt > 0 ? "▲" : dlt < 0 ? "▼" : "•"} {Math.abs(dlt).toFixed(1)}</span> : null}
              </div>
              <div className="cv-kpisub">{META[tab][bucket]}{on ? " · vs " + bLabel : ""}</div>
            </div>
          </div>

          <div className="cv-tblwrap">
            <table className="cv-tbl">
              <thead><tr>
                <th>Customer</th><th>Store</th><th>{ord ? "Order #" : "Latest order #"}</th>
                <th className="r">Order value</th><th>Date</th><th className="r">Orders</th><th className="r">Lifetime ₹</th>
              </tr></thead>
              <tbody>
                {loadingRows ? (<tr><td colSpan="7" className="cv-empty">Loading…</td></tr>)
                  : rows.length === 0 ? (<tr><td colSpan="7" className="cv-empty">No customers in this stage for the selected range.</td></tr>)
                    : rows.map((r, i) => (
                      <tr key={(r.customerID) + "-" + (r.lastOrderID)}>
                        <td><div className="cv-cust">
                          <div className="cv-ava" style={{ background: AV[i % AV.length] }}>{initc(r.customerName)}</div>
                          <div><div className="nm">{r.customerName || "—"}</div><div className="mb">{r.mobileNo}</div></div>
                        </div></td>
                        <td>{r.store || "—"}</td>
                        <td><span className="cv-oid">#{r.lastOrderID}</span></td>
                        <td className="r cv-val">{inr(r.lastOrderValue)}</td>
                        <td>{r.lastOrderDate}</td>
                        <td className="r"><b>{r.orderCount}</b></td>
                        <td className="r">{inr(r.lifetimeValue)}</td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>

          <div className="cv-foot">
            <span>{total ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${nfmt(total)}` : "—"}</span>
            <div className="cv-pager">
              <button disabled={page <= 1} onClick={() => this.setPage(page - 1)}>‹</button>
              <span className="cv-pageno">{page} / {pages}</span>
              <button disabled={page >= pages} onClick={() => this.setPage(page + 1)}>›</button>
            </div>
          </div>
        </section>
      </div>
    );
  }
}

const CSS = `
.cv-wrap{padding:6px 2px 30px;font-variant-numeric:tabular-nums;color:#1d2330}
.cv-h1{font-size:22px;font-weight:700;letter-spacing:-.3px;margin:0}
.cv-h1 .cv-ey{color:#9aa1af;font-weight:600}
.cv-tabs{display:inline-flex;gap:4px;margin:14px 0 4px;border-bottom:2px solid #eef0f5;width:100%}
.cv-tabs button{border:0;background:transparent;padding:9px 16px;font-size:14px;font-weight:600;color:#9aa1af;cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-2px}
.cv-tabs button.on{color:#d6136e;border-bottom-color:#d6136e}
.cv-sub{color:#69707e;font-size:13.5px;margin:8px 0 0;max-width:96ch}
.cv-regnote{color:#69707e}
.cv-filters{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin:14px 0 0}
.cv-filters:first-of-type{margin-top:16px}
.cv-flabel{font-size:11px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#9aa1af;min-width:92px}
.cv-seg{display:inline-flex;border:1px solid #e6e8ef;border-radius:9px;overflow:hidden;background:#fff}
.cv-seg button{border:0;background:transparent;padding:7px 13px;font-size:12.5px;color:#69707e;cursor:pointer;font-weight:500;border-left:1px solid #eef0f5}
.cv-seg button:first-child{border-left:0}
.cv-seg button.on{background:#1f2330;color:#fff}
.cv-daterng{font-size:12.5px;color:#9aa1af}
.cv-vs{color:#5b46e5;font-weight:600}
.cv-custom{display:inline-flex;align-items:center;gap:7px}
.cv-custom input{border:1px solid #e6e8ef;border-radius:8px;padding:6px 9px;font-size:12.5px}
.cv-custom .cv-arw{color:#9aa1af}
.cv-apply{border:1px solid #1f2330;background:#1f2330;color:#fff;border-radius:8px;padding:6px 12px;font-size:12.5px;cursor:pointer;font-weight:600}
.cv-cmp-tog{display:inline-flex;align-items:center;gap:6px;font-size:12.5px;color:#3a4152;cursor:pointer;font-weight:500}
.cv-cmp-tog.off{color:#c1c6d0;cursor:not-allowed}
.cv-stages{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:16px 0}
.cv-stage{text-align:left;background:#fff;border:1px solid #e6e8ef;border-radius:13px;padding:13px 15px;cursor:pointer;position:relative}
.cv-stage:hover{border-color:#c7ccd6}
.cv-stage .cv-lab{font-size:12px;font-weight:600;color:#69707e;display:flex;align-items:center;gap:7px}
.cv-stage .cv-dot{width:9px;height:9px;border-radius:50%;flex:none}
.cv-stage .cv-n{font-size:25px;font-weight:700;line-height:1.1;margin-top:7px}
.cv-stage .cv-meta{font-size:11.5px;color:#9aa1af;margin-top:2px}
.cv-stage[aria-selected=true]{border-color:#d6136e;box-shadow:0 0 0 2px #fde3ef}
.cv-stage .cv-pct{position:absolute;top:12px;right:14px;font-size:14.5px;font-weight:800;letter-spacing:-.3px}
.cv-meta .cv-pctnum{font-weight:700;color:#1d2330}
.cv-mini{width:100%;border-collapse:collapse;margin-top:9px}
.cv-mini td{padding:3px 0;font-size:12px;vertical-align:middle}
.cv-mini .lb{color:#69707e;font-weight:600;white-space:nowrap}
.cv-mini .cn{text-align:right;color:#1d2330;font-weight:700;padding:3px 10px;font-size:15px}
.cv-mini .pv{text-align:right;font-weight:700}
.cv-mini .dl{text-align:right;padding-left:8px;white-space:nowrap}
.cv-mini tr:first-child td{border-bottom:1px solid #f1f2f7;padding-bottom:5px}
.cv-mini tr:last-child td{padding-top:5px}
.cv-delta{font-weight:700;font-size:11px;display:inline-flex;align-items:center;gap:2px}
.cv-delta.up{color:#12a150}.cv-delta.down{color:#e11d48}
.cv-charts{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:16px}
.cv-chart{background:#fff;border:1px solid #e6e8ef;border-radius:13px;padding:12px 14px 8px;min-width:0}
.cv-cht{font-size:11px;font-weight:700;letter-spacing:.03em;text-transform:uppercase;color:#9aa1af;margin-bottom:8px}
.cv-chempty{color:#c1c6d0;font-size:12px;padding:30px 0;text-align:center}
.cv-kpi .cv-kpisub{font-size:10.5px;color:#9aa1af;margin-top:2px}
.cv-kpi .v .cv-delta{margin-left:8px;font-size:12px}
.cv-card{background:#fff;border:1px solid #e6e8ef;border-radius:14px;overflow:hidden}
.cv-cardhd{display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding:14px 18px;border-bottom:1px solid #eef0f5}
.cv-cardhd h2{font-size:15px;margin:0;font-weight:700}
.cv-cardhd .cv-desc{font-size:12px;color:#9aa1af}
.cv-cardhd .cv-right{margin-left:auto;display:flex;gap:8px}
.cv-search{border:1px solid #e6e8ef;border-radius:8px;padding:7px 11px;font-size:12.5px;min-width:190px}
.cv-exp{border:1px solid #e6e8ef;background:#fff;color:#69707e;border-radius:8px;padding:7px 13px;font-size:12.5px;cursor:pointer;font-weight:600}
.cv-strip{display:grid;grid-template-columns:repeat(4,1fr);border-bottom:1px solid #eef0f5}
.cv-kpi{padding:13px 18px;border-right:1px solid #eef0f5}
.cv-kpi:last-child{border-right:0}
.cv-kpi .l{font-size:11px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:#9aa1af}
.cv-kpi .v{font-size:19px;font-weight:700;margin-top:3px}
.cv-tblwrap{overflow-x:auto}
.cv-tbl{width:100%;border-collapse:collapse;font-size:13px;min-width:760px}
.cv-tbl thead th{text-align:left;font-size:11px;letter-spacing:.04em;text-transform:uppercase;color:#9aa1af;font-weight:600;padding:11px 18px;background:#eef0f5}
.cv-tbl th.r,.cv-tbl td.r{text-align:right}
.cv-tbl tbody td{padding:11px 18px;border-bottom:1px solid #eef0f5;vertical-align:middle}
.cv-tbl tbody tr:hover{background:#f7f8fb}
.cv-cust{display:flex;align-items:center;gap:11px}
.cv-ava{width:33px;height:33px;border-radius:50%;flex:none;display:grid;place-items:center;font-weight:700;font-size:12.5px;color:#fff}
.cv-cust .nm{font-weight:600}.cv-cust .mb{font-size:12px;color:#9aa1af}
.cv-oid{font-weight:600;color:#5b46e5}
.cv-val{font-weight:700}
.cv-empty{padding:26px 18px;text-align:center;color:#9aa1af}
.cv-foot{display:flex;align-items:center;justify-content:space-between;padding:11px 18px;font-size:12.5px;color:#9aa1af;flex-wrap:wrap;gap:8px}
.cv-pager{display:flex;gap:8px;align-items:center}
.cv-pager button{border:1px solid #e6e8ef;background:#fff;color:#69707e;width:30px;height:30px;border-radius:7px;cursor:pointer}
.cv-pager button:disabled{opacity:.4;cursor:default}
.cv-pageno{font-size:12.5px;color:#69707e}
@media (max-width:1000px){.cv-charts{grid-template-columns:repeat(2,1fr)}}
@media (max-width:760px){.cv-stages{grid-template-columns:repeat(2,1fr)}.cv-strip{grid-template-columns:repeat(2,1fr)}.cv-charts{grid-template-columns:repeat(2,1fr)}.cv-flabel{min-width:0}}
`;
